import React, { useState, useEffect } from "react";
import AdminLayout from "@/components/layouts/AdminLayout";
import { customerService } from "@/services/CRMService";
import {
    Users,
    Search,
    Filter,
    Mail,
    Phone,
    TrendingUp,
    UserPlus,
    Trash2,
    History,
    ChevronRight,
    ArrowUpRight,
    Star,
    Clock,
    UserCheck,
    UserX
} from "lucide-react";
import { Button, Badge, CardGlass, StatCard, SectionHeader, TablePro, Skeleton } from "@/components/ui/PremiumComponents";
import { toast } from "sonner";

export default function AdminCRM() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [segmentFilter, setSegmentFilter] = useState("");

    useEffect(() => {
        loadCustomers();
    }, [segmentFilter]);

    const loadCustomers = async () => {
        setLoading(true);
        try {
            const data = await customerService.getCustomers(segmentFilter, searchTerm);
            setCustomers(data || []);
        } catch (error) {
            console.error(error);
            toast.error("Error al cargar la base de datos de clientes");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        loadCustomers();
    };

    const getSegmentStatus = (segment) => {
        switch (segment) {
            case 'VIP': return 'success';
            case 'REPEAT': return 'primary';
            case 'NEW': return 'warning';
            case 'INACTIVE': return 'danger';
            default: return 'default';
        }
    };

    const handleDeleteGDPR = async (phone) => {
        if (!window.confirm("¿Estás seguro? Esta acción eliminará los datos personales según GDPR y no se puede deshacer.")) return;

        try {
            await customerService.deleteDataGDPR(phone);
            toast.success("Datos eliminados correctamente (Cumplimiento GDPR)");
            loadCustomers();
        } catch (error) {
            toast.error("Error al procesar la solicitud GDPR");
        }
    };

    // Calculate aggregated stats from current list
    const stats = {
        total: customers.length,
        vips: customers.filter(c => c.customer_segment === 'VIP').length,
        avgTicket: customers.length > 0 ? (customers.reduce((acc, c) => acc + (c.average_ticket || 0), 0) / customers.length) : 0,
        inactive: customers.filter(c => c.customer_segment === 'INACTIVE').length
    };

    return (
        <AdminLayout title="Fidelización & CRM">
            <div className="max-w-[1600px] mx-auto space-y-8 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
                
                <SectionHeader 
                    title="Gestión de Clientes"
                    subtitle="Monitorea el comportamiento de tus comensales y optimiza tus campañas de fidelización con inteligencia de datos."
                    actions={
                        <Button className="group">
                            <UserPlus className="w-4 h-4 mr-2 transition-transform group-hover:scale-110" />
                            Nuevo Cliente
                        </Button>
                    }
                />

                {/* Quick Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard 
                        label="Total Comunidad"
                        value={stats.total}
                        icon={<Users className="w-6 h-6" />}
                        color="var(--primary)"
                        delta={12}
                    />
                    <StatCard 
                        label="Clientes VIP"
                        value={stats.vips}
                        icon={<Star className="w-6 h-6" />}
                        color="#F59E0B"
                        delta={5}
                    />
                    <StatCard 
                        label="Ticket Promedio"
                        value={`€${stats.avgTicket.toFixed(2)}`}
                        icon={<TrendingUp className="w-6 h-6" />}
                        color="#10B981"
                        trend="up"
                        delta={8}
                    />
                    <StatCard 
                        label="Tasa de Inactivos"
                        value={stats.inactive}
                        icon={<UserX className="w-6 h-6" />}
                        color="#EF4444"
                        trend="down"
                        delta={-2}
                    />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                    {/* Filters Sidebar */}
                    <div className="xl:col-span-1 space-y-6">
                        <CardGlass title="Filtros Inteligentes" subtitle="Refina tu búsqueda">
                            <form onSubmit={handleSearch} className="space-y-4">
                                <div>
                                    <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-2 block">Búsqueda Directa</label>
                                    <div className="relative group">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-primary transition-colors" />
                                        <input
                                            type="text"
                                            placeholder="Nombre, Tlf o Email..."
                                            className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:outline-none focus:ring-2 ring-primary/20 transition-all"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-2 block">Segmentación</label>
                                    <div className="grid grid-cols-1 gap-2">
                                        {[
                                            { id: "", label: "Todos los clientes", icon: <Users className="w-3.5 h-3.5" /> },
                                            { id: "VIP", label: "Clientes VIP", icon: <Star className="w-3.5 h-3.5" /> },
                                            { id: "REPEAT", label: "Recurrentes", icon: <UserCheck className="w-3.5 h-3.5" /> },
                                            { id: "NEW", label: "Nuevos Registros", icon: <UserPlus className="w-3.5 h-3.5" /> },
                                            { id: "INACTIVE", label: "En Riesgo (Inactivos)", icon: <UserX className="w-3.5 h-3.5" /> },
                                        ].map((opt) => (
                                            <button
                                                key={opt.id}
                                                type="button"
                                                onClick={() => setSegmentFilter(opt.id)}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all text-left ${
                                                    segmentFilter === opt.id 
                                                    ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-[1.02]' 
                                                    : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
                                                }`}
                                            >
                                                <span className={segmentFilter === opt.id ? 'text-white' : 'text-slate-500'}>
                                                    {opt.icon}
                                                </span>
                                                {opt.label}
                                                {segmentFilter === opt.id && <ChevronRight className="ml-auto w-4 h-4" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <Button type="submit" variant="secondary" className="w-full mt-4">
                                    Aplicar Filtros
                                </Button>
                            </form>
                        </CardGlass>

                        {/* Automation Hint */}
                        <div className="bg-primary/5 border border-primary/20 p-5 rounded-2xl relative overflow-hidden group">
                           <div className="absolute top-0 right-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl -mr-10 -mt-10" />
                           <h4 className="text-white font-black text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                               <Clock className="w-3.5 h-3.5 text-primary" /> Motor CRM Activo
                           </h4>
                           <p className="text-slate-400 text-[11px] leading-relaxed relative z-10">
                               El sistema recalcula automáticamente los segmentos cada 24h basado en la frecuencia de compra y el ticket medio.
                           </p>
                        </div>
                    </div>

                    {/* Customers Table */}
                    <div className="xl:col-span-3">
                        <TablePro 
                            headers={["Perfil del Cliente", "Status", "Última Actividad", "Patrón de Gasto", ""]}
                            data={customers}
                            loading={loading}
                            emptyText="No se encontraron clientes que coincidan con los criterios."
                            renderRow={(customer) => (
                                <>
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 flex items-center justify-center text-white font-black text-xs shadow-inner">
                                                {customer.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??'}
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-white font-bold tracking-tight">{customer.name}</span>
                                                <div className="flex items-center gap-3 mt-1 opacity-60">
                                                    <span className="text-[10px] flex items-center gap-1 font-bold"><Phone className="w-2.5 h-2.5" /> {customer.phone}</span>
                                                    {customer.email && <span className="text-[10px] flex items-center gap-1 font-bold"><Mail className="w-2.5 h-2.5" /> {customer.email.slice(0, 15)}...</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <Badge status={getSegmentStatus(customer.customer_segment)}>
                                            {customer.customer_segment}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-2 text-slate-400">
                                            <Clock className="w-3.5 h-3.5 opacity-40" />
                                            <span className="text-xs font-medium">
                                                {customer.last_order_date ? new Date(customer.last_order_date).toLocaleDateString() : '—'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="flex flex-col">
                                            <div className="flex items-center gap-2">
                                                <span className="text-white font-black text-sm">€{(customer.total_spent || 0).toFixed(2)}</span>
                                                <ArrowUpRight className="w-3 h-3 text-success opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                                                {customer.total_orders} pedidos realizados
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-right">
                                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                                            <Button 
                                                variant="ghost" 
                                                className="h-9 w-9 p-0 flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-all"
                                                title="Ver Historial Detallado"
                                            >
                                                <History className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                className="h-9 w-9 p-0 flex items-center justify-center hover:bg-danger/10 hover:text-danger transition-all"
                                                title="Acción GDPR: Borrado"
                                                onClick={() => handleDeleteGDPR(customer.phone)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </>
                            )}
                        />
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
